import * as React from 'react';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import EventIcon from '@mui/icons-material/Event';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { Lotus } from 'src/icons';

export const ActionIconButton = styled(IconButton, {})<IconButtonProps & React.AnchorHTMLAttributes<HTMLAnchorElement>>(({ theme }) => ({
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
}));

export function CompletedButton({ completed, onToggle }: { completed: boolean, onToggle: () => void }) {
  return (
    <Tooltip title={completed ? '' : 'Oznacz jako ukończone'}>
      <IconButton
        size="small"
        onClick={onToggle}
        color={completed ? 'primary' : 'default'}
      >
        <CheckCircleOutlineIcon />
      </IconButton>
    </Tooltip>
  )
}

export function LikeButton({ liked, onToggle }: { liked: boolean, onToggle?: () => void }) {
  const handleClick = React.useCallback((event: React.MouseEvent) => {
    if (!onToggle) return;
    event.preventDefault();
    onToggle();
  }, [onToggle]);

  return (
    <Tooltip title={liked ? '' : 'Dodaj do ulubionych'}>
      <IconButton
        size="small"
        onClick={handleClick}
        disabled={!onToggle}
        color={liked ? 'secondary' : 'default'}
      >
        {liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
      </IconButton>
    </Tooltip>
  );
}

const LotusIconSlot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 24,
  height: 24,
  minWidth: 24,
  minHeight: 24,
  color: active ? theme.palette.secondary.light : theme.palette.text.secondary,
  '& svg': {
    display: 'block',
    width: 24,
    height: 24,
    flexShrink: 0,
  },
  '& path': active
    ? {
        fill: 'currentColor',
        stroke: 'none',
      }
    : {
        fill: 'none',
        stroke: 'currentColor',
        strokeWidth: 16,
        strokeLinejoin: 'round',
      },
}));

export function PublicLikeButton({
  liked,
  likesCount,
  pending = false,
  onToggle,
}: {
  liked: boolean;
  likesCount: number;
  pending?: boolean;
  onToggle?: () => void;
}) {
  const handleClick = React.useCallback(
    (event: React.MouseEvent) => {
      if (!onToggle || pending) return;
      event.preventDefault();
      onToggle();
    },
    [onToggle, pending],
  );

  return (
    <Tooltip title="Podobała Ci się ta praktyka? Daj nam znać!">
      <IconButton
        size="small"
        onClick={handleClick}
        disabled={!onToggle || pending}
        aria-label="Polub sesję"
        sx={{ flexShrink: 0 }}
      >
        <Box
          display="flex"
          alignItems="center"
          columnGap={1}
          sx={{ flexShrink: 0 }}
        >
          <LotusIconSlot active={liked}>
            <Lotus />
          </LotusIconSlot>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, flexShrink: 0, lineHeight: 1 }}
          >
            {likesCount}
          </Typography>
        </Box>
      </IconButton>
    </Tooltip>
  );
}

interface CalendarSessionProps {
  title: string;
}

export function CalendarButton(props: CalendarSessionProps) {
  const hrefUrl = createCalendarLink(props);
  return (
    <Tooltip title="Dodaj do kalendarza Google">
      <IconButton aria-label="add-to-calendar"
        size="small" target='_blank' href={hrefUrl}
      >
        <EventIcon />
      </IconButton>
    </Tooltip>
  );
}

function createCalendarLink(props: CalendarSessionProps) {
  const returnUrl = window.location.origin + window.location.pathname;
  const url = new URL('https://calendar.google.com/calendar/u/0/r/eventedit');
  url.search = new URLSearchParams({
    text: `${props.title}`,
    details: 'Do zobaczenia na macie!',
    location: returnUrl,
  }).toString();
  return url.toString();
}
